import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';

import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { filter } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import { IsApplicationService } from './is-application.service';

import { Store } from '@ngrx/store';
import {
  ModalConfirmComponent,
  ModalConfirmData,
} from '../../shared/modals/modal-confirm/modal-confirm.component';
import { DeviceTokenType } from '../../shared/models/enums/device-token.enum';
import { AddDeviceTokenPayload } from '../../shared/models/payloads/device-token.payload';
import { LocaleKeys } from '../../shared/utils/locale-keys';
import * as AuthSelectors from '../state/auth-state/auth.selectors';
import { HttpDeviceTokenService } from './http/http-device-token.service';
import { LanguageService } from './language.service';
import { ModalService } from './modal.service';

@Injectable({
  providedIn: 'root',
})
export class AppPushNotificationsService {
  registrationId: string;

  constructor(
    private isApplicationService: IsApplicationService,
    private languageService: LanguageService,
    private modalService: ModalService,
    private httpDeviceTokenService: HttpDeviceTokenService,
    private router: Router,
    private ngZone: NgZone,
    private store: Store
  ) {}

  init() {
    Device.getInfo().then((infos) => {
      if (infos.platform !== 'web') {
        this.store
          .select(AuthSelectors.isLogged)
          .pipe(filter((isLogged) => isLogged))
          .subscribe(() => {
            this.initNotifications();
          });
        this.initPushListeners();
      }
    });
  }

  initNotifications() {
    if (localStorage.getItem(LocaleKeys.notifsDeviceRegistered) === '1') {
      this.acceptNotifications();
    } else if (localStorage.getItem(LocaleKeys.notifsDeviceRegistered) !== '-1') {
      this.askForNotifications();
    } else {
      // les notifs ont déjà été refusées : on regarde c'était quand et si > 15 jours on redemande
      const lastNotificationsRefusedDate = localStorage.getItem(LocaleKeys.notifsLastRefusedDate);
      const now = new Date();
      if (lastNotificationsRefusedDate) {
        const refusedDate = new Date(parseInt(lastNotificationsRefusedDate, 10));

        const diffTime = Math.abs(refusedDate.getTime() - now.getTime());
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (diffDays > 15) {
          this.askForNotifications();
        }
      } else {
        this.askForNotifications();
      }
    }
  }

  askForNotifications() {
    const modalData: ModalConfirmData = {
      color: 'primary',
      title: $localize`:@@notifications.notifications:Notifications`,
      illustration:
        'assets/assets-site/images/svg-illustrations/dashboard-get-knowledge-anchoring.svg',
      illustrationWidth: 330,
      illustrationHeight: 318,
      icon: 'bell',
      message: `
      <p>${$localize`:@@notifications.edn_peut_t_envoyer:${this.languageService.siteName} peut t'envoyer des notifications pour te prévenir lorsqu'un nouvel examen est en ligne, un nouveau classement est disponible, un rédacteur a répondu à une de tes questions...`}</p>
      <p>${$localize`:@@notifications.tes_preferences_de_notifications:Tes préférences de notifications peuvent être modifiées à tout moment depuis les réglages.`}</p>`,
      confirmButtonText: $localize`:@@notifications.activer:Activer`,
      cancelButtonText: $localize`:@@utils.plus_tard:Plus tard`,
    };
    const dialogRef = this.modalService.openNewDesign(ModalConfirmComponent, modalData);

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.acceptNotifications();
      } else {
        this.refuseNotifications();
      }
    });

    // illustration
  }

  refuseNotifications() {
    const now = new Date();
    localStorage.setItem(LocaleKeys.notifsDeviceRegistered, '-1');
    localStorage.setItem(LocaleKeys.notifsLastRefusedDate, now.getTime().toString());
  }

  acceptNotifications() {
    if (!this.registrationId) {
      this.requestPermissionOnDevice();
    } else {
      this.registerDevice();
    }
  }

  requestPermissionOnDevice() {
    PushNotifications.requestPermissions().then((result) => {
      if (result?.receive === 'granted') {
        PushNotifications.addListener('registration', (token) => {
          localStorage.setItem(LocaleKeys.notifsDeviceRegistered, '1');

          this.registrationId = token.value;

          this.registerDevice();

          if (this.isApplicationService.isAppAndroid()) {
            PushNotifications.createChannel({
              id: environment.pushChannel,
              name: environment.pushName,
              description: environment.pushName,
              importance: 5,
              visibility: 1,
              lights: true,
              vibration: true,
              sound: 'default',
            });
          }

          PushNotifications.removeAllDeliveredNotifications();
        });

        setTimeout(() => {
          if (!this.registrationId) {
            PushNotifications.register();
          }
        }, 500);
      }
    });
  }

  initPushListeners() {
    PushNotifications.addListener('pushNotificationActionPerformed', (actionPerformed) => {
      this.ngZone.run(() => {
        let redirectUrl: string;
        if (actionPerformed.notification?.data?.aps) {
          redirectUrl = actionPerformed.notification.data.aps.redirectUrl;
        } else if (actionPerformed.notification?.data?.redirectUrl) {
          redirectUrl = actionPerformed.notification.data.redirectUrl;
        }

        if (redirectUrl) {
          this.redirectAfterPushNotification(redirectUrl.split(environment.domain).pop());
        }
      });
    });
  }

  registerDevice() {
    Device.getInfo().then((info) => {
      const platform = info.platform;

      if (platform === 'ios') {
        this.httpDeviceTokenService
          .add(new AddDeviceTokenPayload(this.registrationId, DeviceTokenType.IOS))
          .subscribe();
      } else if (platform === 'android') {
        this.httpDeviceTokenService
          .add(new AddDeviceTokenPayload(this.registrationId, DeviceTokenType.ANDROID))
          .subscribe();
      }
    });
  }

  redirectAfterPushNotification(slug: string) {
    this.router.navigateByUrl(slug);
  }
}
